import { PlusOutlined } from "@ant-design/icons"
import { Button, Flex, type InputRef, Popover, Select, Tooltip } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { useRef, useState } from "react"

import { type TemplateDocumentFormData } from "../../../../types/template-document-form-data.tsx"
import { TextInput } from "../../../../../../components/Input/TextInput.tsx"

const COL_TYPE_OPTIONS = [
  { label: "Text", value: "text" },
  { label: "Number", value: "number" }
]

interface CustomTableBlockAddColumnPopoverProps {
  form: FormInstance<TemplateDocumentFormData>
  isDuplicateName: (name: string) => boolean
  getColumns: () => { name: string; col_type: string }[]
  getRows: () => Record<string, any>[]
  columnsPath: string[]
  rowsPath: string[]
  validColumns: { name: string; col_type: string }[]
  iconOnly?: boolean
}

export function CustomTableBlockAddColumnPopover({
  form,
  isDuplicateName,
  getColumns,
  getRows,
  columnsPath,
  rowsPath,
  validColumns,
  iconOnly
}: CustomTableBlockAddColumnPopoverProps) {
  const inputRef = useRef<InputRef>(null)

  const [addColOpen, setAddColOpen] = useState(false)
  const [newColName, setNewColName] = useState("")
  const [newColType, setNewColType] = useState("text")

  const trimmedNewCol = newColName.trim()
  const isDuplicate = isDuplicateName(trimmedNewCol)

  const handleAddColumn = () => {
    const trimmed = newColName.trim()
    if (!trimmed || isDuplicateName(trimmed)) return

    const cols = [...getColumns(), { name: trimmed, col_type: newColType }]
    const rows = getRows().map((row) => ({ ...row, [trimmed]: undefined }))

    form.setFields([
      { name: columnsPath, value: cols, touched: true },
      { name: rowsPath, value: rows, touched: true }
    ])

    setNewColName("")
    setNewColType("text")
    setAddColOpen(false)
  }

  return (
    <Popover
      open={addColOpen}
      onOpenChange={(open) => {
        setAddColOpen(open)
        if (open) {
          setTimeout(() => inputRef.current?.focus(), 0)
        }
      }}
      trigger="click"
      placement={validColumns?.length ? "topLeft" : "top"}
      classNames={{ root: "w-96" }}
      content={
        <Flex vertical gap={8}>
          <Tooltip
            title={isDuplicate ? "Column with this name already exists" : ""}
          >
            <TextInput
              // The duplicate-name check right below is an exact,
              // case-sensitive match against existing column names — forcing
              // this field's first letter capital would make it impossible
              // to type a name matching an existing lowercase-starting one.
              disableAutoCapitalize
              ref={inputRef}
              placeholder="Column name"
              value={newColName}
              onChange={(e) => setNewColName(e.target.value)}
              onPressEnter={handleAddColumn}
              status={isDuplicate ? "error" : undefined}
              maxLength={100}
              showCount
              autoFocus
            />
          </Tooltip>

          <Select
            options={COL_TYPE_OPTIONS}
            value={newColType}
            onChange={setNewColType}
          />
          <Button
            type="primary"
            size="small"
            onClick={handleAddColumn}
            disabled={!trimmedNewCol || isDuplicate}
          >
            Add
          </Button>
        </Flex>
      }
    >
      <Tooltip title={iconOnly ? "" : "Add Column"}>
        <Button
          icon={<PlusOutlined />}
          {...(!validColumns.length && { block: true })}
        >
          {iconOnly ? "" : "Add column"}
        </Button>
      </Tooltip>
    </Popover>
  )
}
